import { Box, Stack } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  CourseStructureCtx,
  CustomGradariusActivity,
  DbUser,
  Lti1p0AuthParams,
  Lti1p0LaunchParams,
  Lti1p0Form,
  UserCtx,
} from "../../interfaces";
import { Course, Module, Question, Target, TargetSkill } from "../utils/CourseTree";
import { BarMessageProps, Lti1p3BotNav } from "./Lti1p3BotNav";
import { Lti1p3Solver } from "./Lti1p3Solver";
import { Lti1p3TopNav } from "./Lti1p3TopNav";
import axios from "../../services/axios";
import {
  CourseMetadata,
  Lti1p3LaunchParams,
  LtiOptionActionParams,
  Skill,
} from "@backend-types";
import { Lti1p3EndTarget } from "./dialogs/Lti1p3EndTarget";
import { Lti1p3EndModule } from "./dialogs/Lti1p3EndModule";
import { HelpAndResourcesOptions, QuestionType, Role, _AuthType } from "../../enums";
import { useHelpAndResources } from "../../hooks/useHelpAndResources";
import { MacsAuthenticator } from "../../hooks/useMacsAuthenticator";
import oauthSignature from "oauth-signature";
import { Lti1p0Solver } from "./Lti1p0Solver";
import { SkillVideoModal } from "../v2/SkillVideoModal";
import { GPPVideo } from "../GPPVideo";
import { GPPText } from "../GPPText";
import { GPPLearningPreference } from "../GPPLearningPreference";
import { CheckWorkSkills } from "../v2/learning/CheckWorkSkills";
import { WrapperSubmitReminder } from "../v2/learning/WrapperSubmitReminder";
import useStorageState from "../../hooks/useStorageState";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    width: "100vw",
    maxHeight: "100vh",
    maxWidth: "100vw",
  },
}));

interface Lti1p3LxpProps {
  currentUser: DbUser;
  currentQuestion?: Question | null;
  courseStructureCtx: CourseStructureCtx;
  userCtx: UserCtx;
  mode?: "1p3" | "1p0";
}

export interface Lti1p3LaunchError {
  title: string;
  description: string;
}

const defaultValues: Lti1p0AuthParams = {
  consumerKey: process.env.REACT_APP_GRADARIUS_CONSUMER_KEY!,
  sharedSecret: process.env.REACT_APP_GRADARIUS_SHARED_SECRET!,
  ltiLaunchUrl: process.env.REACT_APP_GRADARIUS_URL!,
  formMethod: "post",
};
/**These are temporary until we implement scalable solution */
const blueBottleValues: Lti1p0AuthParams = {
  consumerKey: process.env.REACT_APP_GRADARIUS_BB_CONSUMER_KEY!,
  sharedSecret: process.env.REACT_APP_GRADARIUS_BB_SHARED_SECRET!,
  ltiLaunchUrl: process.env.REACT_APP_GRADARIUS_BB_URL!,
  formMethod: "post",
};

export const Lti1p3Lxp = (props: Lti1p3LxpProps) => {
  const { mode = "1p3" } = props;
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const qRetry = queryParams.get("retry");
  const qCheckWork = queryParams.get("checkWork");
  const qTrySimilar = queryParams.get("trySimilar");

  const { courseStructureCtx, currentQuestion, userCtx } = props;
  const [currentUser] = useState(props.currentUser);
  const currentTarget = currentQuestion?.parent as Target | null;
  const [targetLastSeen, setTargetLastSeen] = useStorageState({
    storageKey: "lxp-lastTarget",
  });
  const [, setLastCourseFaqTag] = useStorageState({
    storageKey: "lxp-lastCourseFaqTag",
  });
  const currentTargetQuestions = currentTarget?.getTargetQuestions() as Question[];
  const currentTargetSkills = currentTarget?.getAllTargetSkills() ?? [];
  const currentModule = currentTarget?.parent as Module | null;
  const currentCourse = currentModule?.parent as Course | null;
  // next in tree
  const nextModule = courseStructureCtx.courseTreeStructure?.getNextModule(
    currentModule?.id,
    userCtx.studentCurrentClass?.is_demo ?? false
  ) as Module | null;
  const nextTarget = nextModule?.getFirstTarget() as Target | undefined;
  const nextQuestion = courseStructureCtx.courseTreeStructure?.getNextQuestion(
    currentQuestion?.id,
    userCtx.studentCurrentClass?.is_demo ?? false
  ) as Question | null;
  //
  const metadata = useMemo(() => {
    return currentCourse?.getMetadata() as CourseMetadata | undefined;
  }, [currentCourse]);
  // refs
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const userCtxRef = useRef(userCtx);
  userCtxRef.current = userCtx;
  const courseStructureCtxRef = useRef(courseStructureCtx);
  courseStructureCtxRef.current = courseStructureCtx;
  // lti state
  const [lti1p3LaunchParams, setLti1p3LaunchParams] = useState<Lti1p3LaunchParams | null>(
    null
  );
  const [lti1p0LaunchParams, setLti1p0LaunchParams] = useState<Lti1p0LaunchParams | null>(
    null
  );
  const [ltiLaunchError, setLtiLaunchError] = useState<Lti1p3LaunchError | null>(null);
  // ui state
  const [showBottomNav, setShowBottomNav] = useState<boolean>(false);
  const [showRemediation, setShowRemediation] = useState<boolean>(false);
  const [guidedPreference, setGuidedPreference] = useState<boolean | null>(null);
  const [checkWorkMode, setCheckWorkMode] = useState<boolean>(false);
  const [questionSubmitReminderOpen, setQuestionSubmitReminderOpen] = useState(false);
  const [handleNavigateAwayCallBack, setHandleNavigateAwayCallBack] = useState<
    () => () => void
  >(() => () => {});
  const [hasInteractedThreshold, setHasInteractedThreshold] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  // ID of last problem the bar was seen on
  const [seenRemediationBar, setSeenRemediationBar] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<BarMessageProps | null>(null);
  const [remediationReminder, setRemediationReminder] = useState(false);
  const [remediationReminderDot, setRemediationReminderDot] = useState(false);
  const [remediationSkills, setRemediationSkills] = useState<Array<Skill | TargetSkill>>(
    []
  );
  const [targetSkillsModalOpen, setTargetSkillsModalOpen] = useState(false);
  // location of the state will likely change depending on how WS grade events are handled
  const [endOfTarget, setEndOfTarget] = useState<Target | null | undefined>(null); // if not null shows target end modal
  const [endOfModule, setEndOfModule] = useState<Module | null | undefined>(null); // if not null shows module end modal

  const moduleQuizSubmissions = useMemo(() => {
    if (!currentModule) return [];
    const moduleTargetIds: string[] = currentModule
      .getAllTargets()
      .map((item) => item.id);
    return userCtx.userQuizSubmissions.filter((item) =>
      moduleTargetIds.includes(item.target_id)
    );
  }, [currentModule, userCtx.userQuizSubmissions]);

  const targetQuizSubmissions = useMemo(() => {
    if (!currentTarget) return [];
    return userCtx.userQuizSubmissions.filter(
      (item) => item.target_id === currentTarget.id
    );
  }, [currentTarget, userCtx.userQuizSubmissions]);

  const isSolverLoaded = false;
  const hasSubmission: boolean = userCtx.userSubmissions.find(
    (item) => item.question_id === currentQuestion?.id
  )
    ? true
    : false;
  const hasRetry = userCtx.tryAgainSubmissions.has(currentQuestion?.id ?? "");
  const isMaxScore = currentQuestion?.is_question_perfect;
  const isMasteryAndPerfect =
    hasSubmission &&
    currentQuestion?.type === QuestionType.mastery &&
    !hasRetry &&
    isMaxScore;

  useEffect(() => {
    setGuidedPreference(userCtx.gppTextMode ?? null);
  }, [userCtx.gppTextMode]);

  const handleSelectGPPPreference = (preferText: boolean) => {
    userCtx.updateUserMetadata({ le_screen_gpp_prefer_text: preferText });
  };

  const handleDismissAdditionalLearning = () => {
    userCtx.updateUserMetadata({ le_dismissed_additional_learning: true });
  };

  const launchLti1p0Activity = useCallback(
    async (options?: LtiOptionActionParams) => {
      if (!currentQuestion) {
        console.error("No current question to launch");
        return;
      }
      if (!currentUser) {
        console.error("No current user");
        return;
      }
      console.warn("Launching LTI 1p0 Activity");
      // clear any launch errors
      setLtiLaunchError(null);

      // update launch params which triggers submission of form in solver component
      if (currentQuestion.activity_type === "archie_direct_url") {
        const rawUrl = currentQuestion.problem_id ?? currentQuestion.template_id ?? "";

        const url = new URL(rawUrl);
        const queryParams = new URLSearchParams(url.search);
        const formFields: Lti1p0Form = {};
        queryParams.forEach((value, key: string) => {
          formFields[key] = value;
        });

        setLti1p0LaunchParams({
          launchId: `${currentQuestion.id}-${new Date().getTime()}`,
          formFields: formFields,
          launchValues: {
            consumerKey: "",
            sharedSecret: "",
            ltiLaunchUrl: rawUrl,
            formMethod: "get",
          },
        });
      } else {
        const timestamp = Math.round(Date.now() / 1000);
        const useBlueBottleLti = metadata?.use_bb_gradarius_endpoint ?? false;
        // activity details
        const customParams: CustomGradariusActivity = {
          ...(currentQuestion.problem_id && { problem_id: currentQuestion.problem_id }),
          ...(currentQuestion.template_id &&
            !currentQuestion.problem_id && { template_id: currentQuestion.template_id }),
          activity_type: currentQuestion.activity_type,
          hide_score: true,
          // trigger retry
          // mastery problem/algorithmic problems need new_problem flag as well
          ...(options?.retry && { new_solution: true }),
          // trigger check work mode
          ...(options?.checkWork && { check_work: true }),
          // TBD for try similar
          ...(options?.trySimilar && {
            new_solution: true,
            new_problem: true,
          }),
        };
        // auth details - blue bottle/MAT265/MAT210 use a different Gradarius environment
        const consumerKey = useBlueBottleLti
          ? blueBottleValues.consumerKey
          : defaultValues.consumerKey;
        const sharedSecret = useBlueBottleLti
          ? blueBottleValues.sharedSecret
          : defaultValues.sharedSecret;
        const ltiLaunchUrl = useBlueBottleLti
          ? blueBottleValues.ltiLaunchUrl
          : defaultValues.ltiLaunchUrl;
        const formMethod = useBlueBottleLti
          ? blueBottleValues.formMethod
          : defaultValues.formMethod;

        // Assume student is only in one class, append class info with LTI context fields
        let contextId;
        let contextTitle;
        let contextLabel;
        if (userCtx.studentCurrentClass) {
          const studentClass = userCtx.studentCurrentClass;
          const classId = studentClass?.id;
          const classPeriodBlock = studentClass?.period_block;
          const classStartDate = studentClass?.start_date_str;
          const classSubject = studentClass?.course?.name;
          contextId = String(classId);
          contextTitle = `${classPeriodBlock} ${classStartDate}`;
          contextLabel = classSubject;
        }

        const formFields: Lti1p0Form = {
          // OAuth 1.0a Required Parameters
          oauth_consumer_key: consumerKey,
          oauth_nonce: btoa(timestamp + ""),
          oauth_signature_method: "HMAC-SHA1",
          oauth_timestamp: timestamp,
          oauth_version: "1.0",

          // LTI Required Parameters
          lti_message_type: "basic-lti-launch-request",
          lti_version: "LTI-1p0",
          user_id: currentUser.lti_use_uuid ? currentUser.id : currentUser.email,
          user_email: currentUser.email,
          resource_link_id:
            currentQuestion.id +
            (!userCtx.studentCurrentClass ? "" : `-${userCtx.studentCurrentClass.id}`),

          // Custom Gradarius Parameters
          custom_grd_activity: JSON.stringify(customParams),
          resource_link_title: currentQuestion.name,
          custom_grd_a11y: String(currentUser.accessibility),
          custom_grd_high_contrast: String(currentUser.accessibility),
          // context
          context_id: contextId,
          context_title: contextTitle,
          context_label: contextLabel,
        };

        setLti1p0LaunchParams({
          launchId: `${currentQuestion.id}-${new Date().getTime()}`,
          formFields: {
            ...formFields,
            oauth_signature: oauthSignature.generate(
              formMethod,
              ltiLaunchUrl,
              formFields,
              sharedSecret,
              undefined,
              { encodeSignature: false }
            ),
          },
          launchValues: {
            consumerKey: consumerKey,
            sharedSecret: sharedSecret,
            ltiLaunchUrl: ltiLaunchUrl,
            formMethod: formMethod,
          },
        });
        // received launch parameters, launch external activity update question started
        if (
          currentQuestion.type !== QuestionType.guided &&
          !currentQuestion.is_question_started
        )
          userCtxRef.current.markQuestionStarted(currentQuestion.id);
      }
    },
    [
      currentQuestion,
      currentUser,
      metadata?.use_bb_gradarius_endpoint,
      userCtx.studentCurrentClass,
    ]
  );

  const launchLti1p3Activity = useCallback(
    async (options?: LtiOptionActionParams) => {
      if (!currentQuestion) {
        console.error("No current question to launch");
        return;
      }
      if (!currentUser) {
        console.error("No current user");
        return;
      }
      if (iframeRef.current) iframeRef.current.src = "about:blank";
      const currentToolClientId = metadata?.lti_tool_client_id;

      console.warn("Launching LTI 1p3 Activity");
      // clear any launch errors
      setLtiLaunchError(null);

      // backend launch request params
      const activityParams: {
        ltiToolClientId: string;
        activity_id: string;
        options?: LtiOptionActionParams;
      } = {
        ltiToolClientId: currentToolClientId ?? "", // retrieve client_id of external tool from problem or course metadata
        activity_id: currentQuestion.id,
        options: {
          retry: options?.retry ?? Boolean(qRetry),
          checkWork: options?.checkWork ?? Boolean(qCheckWork),
          trySimilar: options?.trySimilar ?? Boolean(qTrySimilar),
        },
      };

      //////

      let ltiLaunchUrl = "/lti/1p3/launch-external";
      if (
        MacsAuthenticator.authHook.authType === _AuthType.LTI &&
        MacsAuthenticator.authHook.ltik
      ) {
        ltiLaunchUrl = "/lti/1p3/launch-external?ltik=" + MacsAuthenticator.authHook.ltik;
      }
      axios
        .post<Lti1p3LaunchParams>(ltiLaunchUrl, activityParams)
        .then((res) => {
          setLti1p3LaunchParams(res.data);
          // received launch parameters, launch external activity update question started
          if (
            currentQuestion.type !== QuestionType.guided &&
            !currentQuestion.is_question_started
          )
            userCtxRef.current.markQuestionStarted(currentQuestion.id);
        })
        .catch((err) => {
          console.error(err);
          setLtiLaunchError({
            title: "Oops!",
            description: "An error occurred while launching your activity",
          });
        });
    },
    [
      currentQuestion,
      currentUser,
      metadata?.lti_tool_client_id,
      qRetry,
      qCheckWork,
      qTrySimilar,
    ]
  );

  useEffect(() => {
    if (userCtx.authHook?.setIframeRef) {
      userCtx.authHook.setIframeRef(iframeRef);
    }
  }, [iframeRef, userCtx.authHook]);

  // On 1p3 question change
  // trigger launch-external endpoint and generate activity parameters
  useEffect(() => {
    console.log("Lti1p3Lxp: currentQuestion changed");
    // launch LTI request
    console.log("CUrrentQuestion", currentQuestion);
    if (currentQuestion) {
      // clear params to allow iframe to show spinner
      setLti1p3LaunchParams(null);
      setLti1p0LaunchParams(null);
      setFeedback(null);
      setShowRemediation(false);
      setSeenRemediationBar(null);
      setRemediationReminder(false);
      setRemediationReminderDot(false);
      setCheckWorkMode(false);
      setRemediationSkills([]);
      setTargetSkillsModalOpen(false);
      setHasInteractedThreshold(0);
      setHasInteracted(false);
      // get new launch params
      switch (mode) {
        case "1p3":
          launchLti1p3Activity();
          break;
        case "1p0":
        default:
          launchLti1p0Activity();
          break;
      }
      userCtxRef.current.upsertUserProgress(currentQuestion.id);
    }
  }, [launchLti1p0Activity, launchLti1p3Activity, currentQuestion, mode]);

  useEffect(() => {
    if (currentQuestion && targetLastSeen !== currentQuestion.target_id) {
      setTargetLastSeen(currentQuestion.target_id);
    }
  }, [currentQuestion, setTargetLastSeen, targetLastSeen]);

  useEffect(() => {
    const faqTag = metadata?.faq_course_tag;
    if (faqTag) {
      setLastCourseFaqTag(faqTag);
    }
  }, [metadata?.faq_course_tag, setLastCourseFaqTag]);

  // On submissions change
  useEffect(() => {
    if (!currentQuestion) return;
    console.log("Lti1p3Lxp: submissions changed");
    if (!currentQuestion.template_id && !currentQuestion.problem_id) {
      setShowBottomNav(true);
      return;
    }
    // look at submission directly to verify as progress recalc could change

    const hasSubmission = userCtx.userSubmissions.find(
      (item) => item.question_id === currentQuestion.id
    )
      ? true
      : false;
    const hasRetry = userCtx.tryAgainSubmissions.has(currentQuestion.id);
    const isMaxScore = currentQuestion.is_question_perfect;

    console.log(hasSubmission, hasRetry, isMaxScore);

    let showBar = false;
    // if (currentQuestion.activity_type === "orchard_node") {
    //   showBar = hasSubmission && !hasRetry && isMaxScore;
    // } else {
    //   showBar = hasSubmission && !hasRetry;
    // }
    showBar = hasSubmission && !hasRetry;
    // isCompleted returns progress property
    // progress property is either 100 if submission or 0 if not
    setShowBottomNav(showBar);
  }, [currentQuestion, userCtx.userSubmissions, userCtx.tryAgainSubmissions]);

  const handleGoBack = () => {
    if (
      !currentQuestion ||
      !courseStructureCtx ||
      !courseStructureCtx.courseTreeStructure
    ) {
      console.warn("Course tree not ready");
      return;
    }
    const parentModule = courseStructureCtx.courseTreeStructure.getModuleByQuestionId(
      currentQuestion.id
    );
    if (!parentModule) {
      console.warn("Parent module not found");
      return;
    }

    if (!userCtxRef.current.dbUser) {
      console.warn("User not found");
      return;
    }

    if (currentQuestion.activity_type === "orchard_node") {
      userCtx.refreshQuizScores(currentQuestion.id);
    }

    if (userCtxRef.current.dbUser.role === Role.student) {
      history.push({
        pathname: "/modules",
        search: new URLSearchParams({
          moduleId: parentModule.id,
        }).toString(),
      });
    } else {
      history.push({
        pathname: "/explore/activities",
        search: new URLSearchParams({
          module: parentModule.id,
          courseId: currentCourse?.id!,
          content: "1",
        }).toString(),
      });
    }
  };

  const isEndOfTarget = useMemo<boolean>(() => {
    if (
      !currentQuestion ||
      !currentTarget ||
      !currentModule ||
      !userCtx.userSubmissions.length
    ) {
      console.warn("Course tree not ready");
      return false;
    }
    return (
      (currentTargetQuestions.length < 2 && currentQuestion.is_question_perfect) ||
      currentTarget.isCompleted() ||
      (currentQuestion.type === QuestionType.mastery &&
        currentQuestion.is_question_submitted &&
        !currentQuestion.is_question_retried)
    );
  }, [
    currentTargetQuestions,
    currentModule,
    currentTarget,
    currentQuestion,
    userCtx.userSubmissions,
  ]);

  const isEndOfModule = useMemo<boolean>(() => {
    if (!currentModule || !userCtx.userSubmissions.length) {
      console.warn("Course tree not ready");
      return false;
    }
    return currentModule.isCompleted();
  }, [currentModule, userCtx.userSubmissions]);

  const showBottomNavModuleHome = useMemo(() => {
    return (
      (metadata?.learning_experience?.skip_target_end && isEndOfModule) ||
      (!metadata?.learning_experience?.skip_target_end &&
        (isEndOfModule || isEndOfTarget))
    );
  }, [isEndOfModule, isEndOfTarget, metadata?.learning_experience?.skip_target_end]);

  const continueButtonText = useMemo(() => {
    if (!currentQuestion || !courseStructureCtx.courseTreeStructure) {
      return "";
    }

    if (
      metadata?.language_overrides?.results_button_text &&
      ((metadata?.learning_experience?.skip_target_end && isEndOfModule) ||
        (!metadata?.learning_experience?.skip_target_end &&
          (isEndOfModule || isEndOfTarget)))
    ) {
      return metadata?.language_overrides?.results_button_text;
    }

    return currentQuestion.activity_type === "orchard_node"
      ? metadata?.language_overrides?.continue_button_text ?? "Go to results"
      : metadata?.language_overrides?.continue_button_text ?? "Continue";
  }, [
    courseStructureCtx.courseTreeStructure,
    currentQuestion,
    metadata?.language_overrides?.continue_button_text,
    metadata?.language_overrides?.results_button_text,
    metadata?.learning_experience?.skip_target_end,
    isEndOfModule,
    isEndOfTarget,
  ]);

  const handleMoveForward = (
    goToProblem: Question | null,
    suppressEoT?: boolean,
    suppressEoM?: boolean,
    options?: LtiOptionActionParams
  ) => {
    if (
      !currentQuestion ||
      !currentTarget ||
      !currentModule ||
      !courseStructureCtx ||
      !courseStructureCtx.courseTreeStructure
    ) {
      console.warn("Course tree not ready");
      return;
    }

    const courseMetadata =
      courseStructureCtx.courseTreeStructure.getCurrentCourseMetadata();
    if (courseMetadata?.learning_experience?.skip_target_end) {
      suppressEoT = true;
    }

    if (suppressEoM) {
      setEndOfModule(null);
    }
    if (suppressEoT) {
      setEndOfTarget(null);
    }

    // still need to determine if EOT should be triggered based on currentTarget.isCompleted or individual question
    // EOM still triggers at < 100% per question
    const isLastQuestionInTarget =
      currentQuestion.id === currentTargetQuestions[currentTargetQuestions.length - 1].id;

    if (currentQuestion.activity_type === "orchard_node") {
      userCtx.refreshQuizScores(currentQuestion.id);
    }

    if (isEndOfTarget && !suppressEoT && isLastQuestionInTarget) {
      setEndOfTarget(currentTarget as Target);
    } else if (isEndOfModule && !suppressEoM && isLastQuestionInTarget) {
      setEndOfModule(currentModule as Module);
    } else {
      let nextQuestion = goToProblem;
      if (!goToProblem) {
        nextQuestion = courseStructureCtx.courseTreeStructure.getNextQuestion(
          currentQuestion.id,
          userCtx.studentCurrentClass?.is_demo ?? false
        );
      }
      if (nextQuestion) {
        const stringifiedOptions = options
          ? Object.fromEntries(
              Object.entries(options).map(([key, value]) => [key, String(value)])
            )
          : {};
        history.push({
          pathname: "/solver",
          search: new URLSearchParams({
            questionId: nextQuestion.id,
            ...stringifiedOptions,
          }).toString(),
        });
      } else {
        console.warn("Next question not found");
        handleGoBack();
      }
    }
  };

  const handleBackToCanvas = () => {
    const returnUrl = MacsAuthenticator.authHook.ltiReturnUrl;
    console.log(`Return URL: ${returnUrl}`);
    if (returnUrl) window.location.href = returnUrl;
  };

  const handleMoveForwardEoT = () => {
    handleMoveForward(null, true, false);
    setEndOfTarget(null);
  };

  const handleReviewEoT = () => {
    if (!currentQuestion) return;
    handleMoveForward(currentQuestion, true, true, { retry: true });
    setEndOfTarget(null);
  };

  const handleMoveForwardEoM = () => {
    if (
      !currentQuestion ||
      !currentModule ||
      !courseStructureCtx ||
      !courseStructureCtx.courseTreeStructure
    ) {
      console.warn("Course tree not ready");
      return;
    }
    const nextModule = courseStructureCtx.courseTreeStructure.getNextModule(
      currentModule.id,
      userCtx.studentCurrentClass?.is_demo ?? false
    ) as Module | null | undefined;
    // if no next module, trigger a force exit, otherwise go to next problem in next module via standard logic
    if (!nextModule) {
      handleGoBack();
    } else {
      const nextTarget = nextModule.getFirstTarget();
      const nextQuestion = nextTarget?.getFirstPracticeProblem();
      if (nextQuestion) {
        history.push({
          pathname: "/solver",
          search: new URLSearchParams({
            questionId: nextQuestion.id,
          }).toString(),
        });
      } else {
        handleGoBack();
      }
    }
    setEndOfModule(null);
  };

  const handleTriggerRemediation = async (skillId: string, triggered?: boolean) => {
    if (!currentQuestion) return;

    const getSkill = async (skillId: string): Promise<Skill> => {
      let skill;
      try {
        const res = await axios.get(`/skill/${skillId}`);
        skill = res.data;
      } catch (err) {
        console.log(err);
      }
      return skill;
    };

    const newRemediationSkills = [...remediationSkills];
    const skill = remediationSkills.find((item) => item.skill_id === skillId);
    if (!skill) {
      if (skillId === "other") {
        if (triggered) {
          newRemediationSkills.push(...currentTargetSkills);
        }
      } else {
        let newSkill: TargetSkill | Skill | undefined = currentTargetSkills.find(
          (item) => item.skill_id === skillId
        );
        if (!newSkill) {
          newSkill = await getSkill(skillId);
        }
        newRemediationSkills.push(newSkill);
      }
    }
    // clear dupes and update remediation skills
    const uniqueSkillsMap = new Map(
      newRemediationSkills.map((skill) => [skill.skill_id, skill])
    );
    const uniqueSkills = Array.from(uniqueSkillsMap.values());
    setRemediationSkills(uniqueSkills);
    if (
      triggered &&
      currentQuestion.id !== seenRemediationBar &&
      !metadata?.learning_experience?.disable_remediation_trigger
    ) {
      setSeenRemediationBar(currentQuestion.id);
      setShowRemediation(true);
    }
  };

  const handleDismissRemediation = () => {
    setShowRemediation(false);
    setRemediationReminder(true);
    if (remediationSkills.length > 0 || currentTargetSkills.length > 0) {
      setRemediationReminderDot(true);
    }
  };

  const handleClickRemediation = () => {
    setShowRemediation(false);
    if (remediationSkills.length > 0 || currentTargetSkills.length > 0) {
      setRemediationReminderDot(true);
      handleOpenSkillModal();
    } else {
      setRemediationReminder(true);
    }
  };

  // checking if student has interracted to show submit work reminder
  const handleIncrementInteraction = () => {
    setHasInteractedThreshold((prevVal) => prevVal + 1);
  };

  useEffect(() => {
    if (hasInteractedThreshold > 2) {
      setHasInteracted(true);
    } else {
      setHasInteracted(false);
    }
  }, [hasInteractedThreshold]);
  // end check

  const handleBottomBarAction = (options: LtiOptionActionParams) => {
    setFeedback(null);
    setLti1p3LaunchParams(null);
    setLti1p0LaunchParams(null);
    switch (mode) {
      case "1p3":
        launchLti1p3Activity(options);
        break;
      case "1p0":
      default:
        launchLti1p0Activity(options);
        break;
    }
  };

  const handleClickCheckWork = () => {
    setCheckWorkMode(true);
    handleBottomBarAction({ checkWork: true });
  };
  const handleClickRetry: (trySimilar?: boolean) => void = async (
    trySimilar?: boolean
  ) => {
    if (!currentQuestion) return;
    try {
      await userCtx.insertUserTryAgain(currentQuestion.id);
      handleBottomBarAction({
        retry: true,
        trySimilar: currentQuestion.type === QuestionType.mastery || trySimilar,
      });
    } catch (e) {}
  };
  const handleClickTrySimilar = () => {
    handleClickRetry(true);
  };

  const handleShowFeedback = (status: "success" | "error") => {
    if (metadata?.learning_experience?.disable_answer_feedback) return;
    if (status === "success") {
      const messageOptions = [
        "Nice one!",
        "Great work!",
        "Awesome!",
        "You nailed it!",
        "Good job!",
        "Way to go!",
      ];
      let randMessage = messageOptions[Math.floor(Math.random() * messageOptions.length)];

      if (metadata?.learning_experience?.override_incorrect_answer_message) {
        randMessage = "Correct";
      }

      setFeedback({
        message: randMessage,
        icon: <i className="fa-solid fa-circle-check"></i>,
        iconColor: "#5BA621",
      });
    } else {
      const messageOptions = [
        "Looks like you might be struggling with this one. Want to try again?",
        "Nice try! Review the material, and try again.",
        "Not quite, but you’re getting there!",
        "Almost! You can use the Study resources button to review the concepts.",
        "You’re on the right track!",
        "That was a good attempt! Use ‘Practice more’ to try again.",
      ];
      if (currentTargetSkills.length > 0) {
        messageOptions.push("Nice try! Consider reviewing the related videos.");
      }
      let randMessage = messageOptions[Math.floor(Math.random() * messageOptions.length)];

      if (metadata?.learning_experience?.override_incorrect_answer_message) {
        randMessage = "Incorrect";
      }

      setFeedback({
        message: randMessage,
        icon: <i className="fa-solid fa-repeat"></i>,
        iconColor: "#FE8B06",
      });
    }
  };

  const handleHideRemediationReminder = () => {
    setRemediationReminder(false);
  };

  const handleOpenSkillModal = () => {
    setTargetSkillsModalOpen(true);
    handleHideRemediationReminder();
  };

  // call this when doing anything that will navigate the user away from the learning experience
  // need to check if the user has done anything on the problem or not
  // if not, trigger the callback function in the parameters
  const handleNavigateAway = (callBack: () => void) => {
    if (hasInteracted && !currentQuestion?.is_question_submitted) {
      setQuestionSubmitReminderOpen(true);
      setHandleNavigateAwayCallBack(() => callBack);
    } else {
      callBack();
    }
  };

  const handleDismissSubmitReminder = () => {
    setQuestionSubmitReminderOpen(false);
  };

  /**
   * ---Needed Functionality---
   * UserQuestionStart updater // added line 87
   * UserProgress updater? - confirm if this is deprecated
   * WS event listener - to receive new submissions via lti grader and update frontend status
   *        Update to listen for new submission event
   * End of target
   * End of module
   * Help and resources
   */
  const { element: HelpAndResourcesButton } = useHelpAndResources({
    yOffset: !showBottomNav && !showRemediation ? undefined : "8em",
    hideHelpAndResourcesOption: [HelpAndResourcesOptions.GUIDED_TOUR],
    hideButton: metadata?.learning_experience?.disable_help_and_resources_button,
  });

  // override solver render if end of target or module
  if (currentQuestion && currentTarget && currentModule) {
    if (endOfTarget) {
      return (
        <Lti1p3EndTarget
          currentModule={currentModule as Module}
          currentTarget={currentTarget as Target}
          currentCourse={currentCourse}
          currentClass={userCtx.studentCurrentClass}
          currentQuestion={currentQuestion}
          moduleQuizSubmissions={moduleQuizSubmissions}
          targetQuizSubmissions={targetQuizSubmissions}
          nextQuestion={nextQuestion}
          nextTarget={nextTarget}
          nextModule={nextModule}
          courseMetadata={currentCourse?.getMetadata()}
          targetMetadata={currentTarget?.getMetadata()}
          currentUser={currentUser}
          handleBackToCanvas={handleBackToCanvas}
          handleReview={handleReviewEoT}
          handleMoveForward={handleMoveForwardEoT}
          handleModuleHome={handleGoBack}
          handleDismissAdditionalLearning={handleDismissAdditionalLearning}
        />
      );
    }

    if (endOfModule) {
      return (
        <Lti1p3EndModule
          currentModule={currentModule as Module}
          currentTarget={currentTarget as Target}
          currentQuestion={currentQuestion}
          moduleQuizSubmissions={moduleQuizSubmissions}
          nextQuestion={nextQuestion}
          nextTarget={nextTarget}
          nextModule={nextModule}
          currentCourse={currentCourse}
          courseMetadata={currentCourse?.getMetadata()}
          currentClass={userCtx.studentCurrentClass}
          currentUser={currentUser}
          handleBackToCanvas={handleBackToCanvas}
          handleGoToProblem={handleMoveForward}
          handleMoveForward={handleMoveForwardEoM}
          handleModuleHome={handleGoBack}
        />
      );
    }
  }

  const getSolver = () => {
    if (!currentQuestion || !currentTarget) return <></>;

    // static GPP loading
    if (currentQuestion.type === QuestionType.guided && currentQuestion.is_static_gpp) {
      if (
        guidedPreference === false &&
        currentQuestion.question_skills &&
        currentQuestion.question_skills.length > 0
      ) {
        return <GPPVideo currentQuestion={currentQuestion} />;
      } else if (guidedPreference === true) {
        return <GPPText currentQuestion={currentQuestion} />;
      } else if (!currentQuestion.template_id && !currentQuestion.problem_id) {
        // added this condition if user has selected video mode preference and no video is available so fallback to text mode so that we don't see the gradarius error page
        return <GPPText currentQuestion={currentQuestion} />;
      }
    }

    // Blue Bottle/MAT210/MAT265 check work video loading
    if (
      currentQuestion.type === QuestionType.practice ||
      currentQuestion.type === QuestionType.mastery
    ) {
      if (checkWorkMode && metadata?.learning_experience?.load_checkwork_videos) {
        return <CheckWorkSkills currentQuestion={currentQuestion} />;
      }
    }

    return (
      <>
        {currentUser && mode === "1p3" ? (
          <>
            {lti1p3LaunchParams ? (
              <Lti1p3Solver
                iframeRef={iframeRef}
                launchParams={lti1p3LaunchParams}
                launchError={ltiLaunchError}
                currentQuestion={currentQuestion}
                currentUser={currentUser}
                userCtx={userCtx}
                userCtxRef={userCtxRef}
                showFeedback={handleShowFeedback}
                handleTriggerRemediation={handleTriggerRemediation}
                handleIncrementInteraction={handleIncrementInteraction}
              />
            ) : (
              <Box
                id="empty"
                sx={{ flexGrow: 1, overflow: "hidden", position: "relative" }}></Box>
            )}
          </>
        ) : (
          <>
            {lti1p0LaunchParams ? (
              <Lti1p0Solver
                key={lti1p0LaunchParams.launchId}
                iframeRef={iframeRef}
                launchParams={lti1p0LaunchParams}
                launchError={ltiLaunchError}
                currentQuestion={currentQuestion}
                currentUser={currentUser}
                userCtx={userCtx}
                userCtxRef={userCtxRef}
                showFeedback={handleShowFeedback}
                handleTriggerRemediation={handleTriggerRemediation}
                handleIncrementInteraction={handleIncrementInteraction}
              />
            ) : (
              <Box
                id="empty"
                sx={{ flexGrow: 1, overflow: "hidden", position: "relative" }}></Box>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <Stack direction="column" className={classes.root}>
        {currentQuestion && currentTarget && (
          <>
            <Lti1p3TopNav
              handleGoBack={() => handleNavigateAway(handleGoBack)}
              handleMoveForward={handleMoveForward}
              handleNavigateAway={handleNavigateAway}
              showTitle={metadata?.learning_experience?.top_bar_title}
              useResourceLinkTitle={
                metadata?.learning_experience?.use_resource_link_title
              }
              variant={metadata?.learning_experience?.top_bar_variant}
              floatingOptions={metadata?.learning_experience?.floating_options}
              showStudentModeBanner={
                currentUser.role !== Role.student &&
                courseStructureCtx.deeplinkMode === null
              }
              allTargetQuestions={currentTargetQuestions}
              currentQuestion={currentQuestion}
              skillVideos={currentTarget.getAllTargetSkills()}
              isSolverLoaded={isSolverLoaded}
              userCtx={userCtx}
              userCtxRef={userCtxRef}
              courseStructureCtx={courseStructureCtx}
              courseStructureCtxRef={courseStructureCtxRef}
              remediationReminder={remediationReminder}
              remediationReminderDot={remediationReminderDot}
              handleOpenSkillModal={handleOpenSkillModal}
              handleHideRemediationReminder={handleHideRemediationReminder}
              metadata={metadata}
            />
            {!metadata?.learning_experience?.disable_help_and_resources_button &&
              HelpAndResourcesButton}
            {currentQuestion.type === QuestionType.guided &&
            guidedPreference === null &&
            (currentQuestion.is_static_gpp ||
              (currentQuestion.question_skills &&
                currentQuestion.question_skills.length > 0)) ? (
              <GPPLearningPreference
                handleSelectGPPPreference={handleSelectGPPPreference}
                currentQuestion={currentQuestion}
              />
            ) : (
              getSolver()
            )}
            {(!ltiLaunchError || true) && (
              <Lti1p3BotNav
                metadata={metadata}
                open={showBottomNav}
                remediationOpen={showRemediation}
                handleMoveForward={handleMoveForward}
                handleModuleHome={handleGoBack}
                handleNavigateAway={handleNavigateAway}
                handleDismissRemediation={handleDismissRemediation}
                handleClickRemediation={handleClickRemediation}
                handleClickCheckWork={handleClickCheckWork}
                handleClickRetry={handleClickRetry}
                handleClickTrySimilar={handleClickTrySimilar}
                feedback={feedback}
                showModuleHome={showBottomNavModuleHome}
                showPracticeMore={
                  currentQuestion.activity_type !== "orchard_node" &&
                  currentQuestion.type !== QuestionType.guided
                }
                showCheckWork={
                  currentQuestion.activity_type !== "archie_direct_url" &&
                  currentQuestion.activity_type !== "lti1p3_problem"
                }
                showRetry={
                  currentQuestion.type !== QuestionType.mastery &&
                  currentQuestion.activity_type !== "archie_direct_url" &&
                  currentQuestion.activity_type !== "lti1p3_problem" &&
                  !isMasteryAndPerfect
                }
                showTrySimilar={
                  (currentQuestion.is_algorithmic_practice_problem ||
                  currentQuestion.template_id
                    ? true
                    : false) && !isMasteryAndPerfect
                }
                continueButtonText={continueButtonText}
              />
            )}
          </>
        )}
      </Stack>
      <SkillVideoModal
        open={targetSkillsModalOpen}
        skills={remediationSkills.length > 0 ? remediationSkills : currentTargetSkills}
        onClose={() => setTargetSkillsModalOpen(false)}
        showControls={true}
        gtmSection={"main navbar - video button"}
      />
      <WrapperSubmitReminder
        promptOpen={questionSubmitReminderOpen}
        promptOnClose={handleDismissSubmitReminder}
        promptOnContinue={handleNavigateAwayCallBack}
      />
    </>
  );
};
